/**
 * Unified input source: pressed keys + edge-detected actions.
 * Handles keyboard + arbitrary external actions (touch buttons) and exposes
 * a tiny API consumed by the engine each frame.
 */
export type Action = "jump" | "duck" | "throw" | "pause";

export interface InputSnapshot {
  duckHeld: boolean;
  jumpEdge: boolean;
  throwEdge: boolean;
  pauseEdge: boolean;
}

export class InputController {
  private duckHeld = false;
  private jumpQueued = false;
  private throwQueued = false;
  private pauseQueued = false;
  private disposed = false;

  private readonly onKeyDown = (e: KeyboardEvent) => {
    if (this.disposed) return;
    switch (e.code) {
      case "ArrowUp":
      case "Space":
      case "KeyW":
        this.jumpQueued = true;
        if (e.code === "Space") e.preventDefault();
        break;
      case "ArrowDown":
      case "KeyS":
        this.duckHeld = true;
        e.preventDefault();
        break;
      case "KeyJ":
      case "KeyF":
      case "Enter":
        this.throwQueued = true;
        break;
      case "KeyP":
      case "Escape":
        this.pauseQueued = true;
        break;
    }
  };

  private readonly onKeyUp = (e: KeyboardEvent) => {
    if (this.disposed) return;
    if (e.code === "ArrowDown" || e.code === "KeyS") {
      this.duckHeld = false;
    }
  };

  private readonly onBlur = () => {
    this.duckHeld = false;
  };

  attach(target: Window = window): void {
    target.addEventListener("keydown", this.onKeyDown, { passive: false });
    target.addEventListener("keyup", this.onKeyUp);
    target.addEventListener("blur", this.onBlur);
  }

  detach(target: Window = window): void {
    this.disposed = true;
    target.removeEventListener("keydown", this.onKeyDown);
    target.removeEventListener("keyup", this.onKeyUp);
    target.removeEventListener("blur", this.onBlur);
  }

  /** Called by external sources (e.g. touch buttons). */
  trigger(action: Action, held?: boolean): void {
    if (this.disposed) return;
    switch (action) {
      case "jump":
        this.jumpQueued = true;
        break;
      case "duck":
        this.duckHeld = !!held;
        break;
      case "throw":
        this.throwQueued = true;
        break;
      case "pause":
        this.pauseQueued = true;
        break;
    }
  }

  /** Drain edges + read held state. */
  consume(): InputSnapshot {
    const snap: InputSnapshot = {
      duckHeld: this.duckHeld,
      jumpEdge: this.jumpQueued,
      throwEdge: this.throwQueued,
      pauseEdge: this.pauseQueued,
    };
    this.jumpQueued = false;
    this.throwQueued = false;
    this.pauseQueued = false;
    return snap;
  }
}
